import { useEffect, useState } from "react";
import { useLocation, Link } from "react-router-dom";
import { API } from "@/lib/api";
import { STRINGS } from "@/lib/i18n";
import { Brand } from "@/components/Brand";
import SeoHead from "@/components/SeoHead";
import PublicFooter from "@/components/PublicFooter";
import { Phone, ExternalLink, ArrowRight, ShieldAlert, Info } from "lucide-react";
import { toast } from "sonner";

const EVIDENCE_LABEL = {
  en: {
    verified_fact: "Verified",
    partner_confirmed: "Confirmed with the agency",
    public_source: "From a public source",
    estimate: "Estimate",
    assumption: "Assumption",
    needs_verification: "Not yet verified",
  },
  es: {
    verified_fact: "Verificado",
    partner_confirmed: "Confirmado con la agencia",
    public_source: "De una fuente pública",
    estimate: "Estimación",
    assumption: "Suposición",
    needs_verification: "Aún no verificado",
  },
};

const EVIDENCE_INTRO = {
  en: "Each listing shows how well we have verified it. Always call to confirm before you go.",
  es: "Cada resultado muestra que tan verificada esta la informacion. Llame siempre para confirmar antes de ir.",
};

function PublicEvidenceLabel({ level, lang }) {
  const key = level && EVIDENCE_LABEL[lang][level] ? level : "assumption";
  const label = EVIDENCE_LABEL[lang][key];
  return (
    <span
      className="inline-flex items-center gap-1 px-2 py-0.5 text-[10px] uppercase tracking-wider font-semibold text-slate-600 bg-slate-100 rounded"
      data-testid={`seeker-evidence-${key}`}
    >
      {label}
    </span>
  );
}

export default function Seeker() {
  const loc = useLocation();
  const lang = loc.pathname.startsWith("/es/") || loc.pathname === "/es" ? "es" : "en";
  const t = STRINGS[lang];
  const [form, setForm] = useState({ situation: "not_sure", area: "", household: "just_me" });
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => { document.documentElement.lang = lang; }, [lang]);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const r = await fetch(`${API}/public/seeker/search`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ ...form, lang }),
      });
      if (!r.ok) throw new Error();
      setResult(await r.json());
      window.scrollTo({ top: 400, behavior: "smooth" });
    } catch { toast.error(lang === "es" ? "No se pudo buscar — inténtelo de nuevo." : "Search failed — please try again."); }
    finally { setBusy(false); }
  };

  const handoffTap = (situation) => {
    fetch(`${API}/public/seeker/handoff`, { method: "POST", headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ situation: form.situation, area: form.area, household: form.household, programs_shown: (result?.programs || []).length, connected: true }) }).catch(() => {});
  };

  return (
    <div className="min-h-screen bg-[#F8FAF9] text-slate-900" data-testid="seeker-page" lang={lang}>
      <SeoHead
        title={lang === "es" ? "Encuentre ayuda de vivienda · Throughline · San Antonio" : "Find housing help · Throughline · San Antonio"}
        description={lang === "es"
          ? "Directorio anónimo de programas de vivienda en el área de San Antonio para veteranos, recuperación, reentrada, violencia doméstica, discapacidad, jóvenes y adultos mayores."
          : "Anonymous directory of housing programs in Greater San Antonio for veterans, recovery, reentry, DV, disability, youth, and seniors. No PII, no application, no queue."}
        canonicalPath={lang === "es" ? "/es/find" : "/find"}
        hreflang={[
          { lang: "en", path: "/find" },
          { lang: "es", path: "/es/find" },
          { lang: "x-default", path: "/find" },
        ]}
      />
      <header className="border-b border-slate-200 bg-white">
        <div className="max-w-3xl mx-auto px-4 py-3 flex items-center justify-between">
          <Link to={lang === "es" ? "/es/find" : "/find"}><Brand variant="dark" size={18} /></Link>
          <Link to={lang === "es" ? "/find" : "/es/find"} className="text-sm underline text-slate-700" data-testid="seeker-lang-switch" aria-label={t.switch_lang_alt}>
            {t.switch_lang}
          </Link>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-4 py-8 md:py-12">
        <h1 className="text-3xl md:text-4xl font-bold leading-tight" data-testid="seeker-title">{t.seeker_title}</h1>
        <p className="mt-3 text-sm md:text-base text-slate-700 leading-relaxed" data-testid="seeker-lede">{t.seeker_lede}</p>

        <form onSubmit={submit} className="mt-6 p-4 md:p-6 bg-white border border-slate-200 rounded-lg space-y-4" data-testid="seeker-form" aria-label={t.seeker_step}>
          <div className="text-[11px] uppercase tracking-widest text-slate-500 font-semibold">{t.seeker_step}</div>

          <div>
            <label className="block text-sm font-semibold mb-1" htmlFor="s-situation">{t.situation_label}</label>
            <select id="s-situation" required value={form.situation} onChange={e=>setForm({...form, situation:e.target.value})}
                    className="w-full border border-slate-300 rounded-md px-3 py-3 text-base bg-white" data-testid="seeker-situation">
              {Object.entries(t.situations).map(([k,v]) => <option key={k} value={k}>{v}</option>)}
            </select>
          </div>

          <div>
            <label className="block text-sm font-semibold mb-1" htmlFor="s-area">{t.where_label}</label>
            <select id="s-area" value={form.area} onChange={e=>setForm({...form, area:e.target.value})}
                    className="w-full border border-slate-300 rounded-md px-3 py-3 text-base bg-white" data-testid="seeker-area">
              <option value="">{lang === "es" ? "Todo el Condado de Bexar" : "All of Bexar County"}</option>
              <option value="78201">78201 — West / Deco District</option>
              <option value="78202">78202 — Eastside</option>
              <option value="78203">78203 — Eastside / Denver Heights</option>
              <option value="78204">78204 — Southtown</option>
              <option value="78207">78207 — Westside</option>
              <option value="78210">78210 — Southeast</option>
              <option value="78211">78211 — South</option>
              <option value="78212">78212 — Monte Vista / Tobin Hill</option>
              <option value="78213">78213 — North Central</option>
              <option value="78214">78214 — South (Harlandale)</option>
              <option value="78215">78215 — Downtown</option>
              <option value="78216">78216 — North Central (Airport)</option>
              <option value="78218">78218 — Northeast</option>
              <option value="78220">78220 — East</option>
              <option value="78221">78221 — South (Hot Wells)</option>
              <option value="78223">78223 — Southeast (Highland Hills)</option>
              <option value="78224">78224 — Southwest</option>
              <option value="78225">78225 — Southwest</option>
              <option value="78227">78227 — West</option>
              <option value="78228">78228 — West (Woodlawn)</option>
              <option value="78229">78229 — Medical Center</option>
              <option value="78230">78230 — Northwest</option>
              <option value="78237">78237 — West</option>
              <option value="78242">78242 — Southwest</option>
              <option value="78244">78244 — Northeast (Kirby)</option>
              <option value="78250">78250 — Far Northwest</option>
              <option value="78251">78251 — Southwest (SeaWorld)</option>
              <option value="78254">78254 — Far Northwest</option>
              <option value="78258">78258 — Stone Oak</option>
              <option value="78259">78259 — Encino Park</option>
              <option value="boerne">Boerne (Kendall)</option>
              <option value="new_braunfels">New Braunfels (Comal)</option>
              <option value="seguin">Seguin (Guadalupe)</option>
              <option value="bexar_unspecified">Somewhere else in Bexar</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-semibold mb-1" htmlFor="s-hh">{t.household_label}</label>
            <select id="s-hh" value={form.household} onChange={e=>setForm({...form, household:e.target.value})}
                    className="w-full border border-slate-300 rounded-md px-3 py-3 text-base bg-white" data-testid="seeker-household">
              {Object.entries(t.households).map(([k,v]) => <option key={k} value={k}>{v}</option>)}
            </select>
          </div>

          <button type="submit" disabled={busy} className="w-full bg-slate-900 hover:bg-slate-800 disabled:opacity-60 text-white text-base font-semibold rounded-md py-3 min-h-[48px]" data-testid="seeker-submit">
            {t.find_button}
          </button>
        </form>

        {result && (
          <>
            {result.crisis && (
              <section className="mt-8 p-5 rounded-lg bg-rose-50 border-2 border-rose-300" data-testid="seeker-crisis" role="alert" aria-live="assertive">
                <div className="flex items-center gap-2 text-rose-900 font-bold text-lg"><ShieldAlert className="w-5 h-5"/>{t.crisis_title}</div>
              </section>
            )}

            <section className="mt-8" data-testid="seeker-handoffs" aria-label={t.handoffs_title}>
              <h2 className="text-xl font-bold mb-3">{t.handoffs_title}</h2>
              <div className="space-y-3">
                {(result.handoffs || []).map((h, i) => (
                  <article key={i} className="block p-4 bg-white border-2 border-emerald-300 rounded-lg" data-testid={`seeker-handoff-${i}`}>
                    <div className="font-semibold text-slate-900">{h.label}</div>
                    <div className="mt-1 flex flex-wrap gap-3 text-sm">
                      {h.phone && <a href={`tel:${h.phone.replace(/[^\d+]/g,"")}`} onClick={()=>handoffTap()} className="text-emerald-800 font-semibold inline-flex items-center gap-1 min-h-[44px] py-2 hover:underline" data-testid={`seeker-call-${i}`}><Phone className="w-4 h-4" aria-hidden="true"/>{t.handoff_call}: {h.phone}</a>}
                      {h.web && <a href={h.web} target="_blank" rel="noreferrer" onClick={()=>handoffTap()} className="text-emerald-800 inline-flex items-center gap-1 min-h-[44px] py-2 hover:underline" data-testid={`seeker-web-${i}`}><ExternalLink className="w-4 h-4" aria-hidden="true"/>{t.handoff_web}</a>}
                    </div>
                    <div className="mt-1 text-xs text-slate-600">{h.note}</div>
                  </article>
                ))}
              </div>
            </section>

            <section className="mt-8" data-testid="seeker-programs" aria-label={t.programs_title}>
              <h2 className="text-xl font-bold mb-3">{t.programs_title}</h2>
              {(result.programs || []).length === 0 ? (
                <div className="p-4 bg-amber-50 border border-amber-300 rounded-md text-sm text-amber-900" data-testid="seeker-empty">{t.empty_msg}</div>
              ) : (
                <>
                <p className="text-sm text-slate-600 leading-relaxed mb-4" data-testid="seeker-evidence-intro">
                  {EVIDENCE_INTRO[lang]}
                </p>
                <div className="space-y-3">
                  {result.programs.map(p => (
                    <article key={p.id} className="p-4 bg-white border border-slate-200 rounded-lg" data-testid={`seeker-program-${p.id}`}>
                      <div className="mb-1.5"><PublicEvidenceLabel level={p.evidence_level} lang={lang} /></div>
                      <h3 className="font-bold text-lg leading-tight">{p.name}</h3>
                      <p className="text-sm text-slate-700 mt-1">{t.who_it_serves}: {p.who_it_serves}</p>
                      <div className="mt-3 text-sm text-slate-800"><span className="font-semibold">{t.what_pays}:</span> {p.what_it_pays_for}</div>
                      <div className="mt-1 text-sm text-slate-800"><span className="font-semibold">{t.wait}:</span> {p.wait_plain}</div>
                      <div className="mt-1 text-sm text-slate-800"><span className="font-semibold">{t.documents}:</span> {p.documents.join(" · ")}</div>
                      {(p.contact.phone || p.contact.address || p.contact.website) && (
                        <div className="mt-3 pt-3 border-t border-slate-100 text-sm">
                          {p.contact.agency && <div className="font-semibold">{p.contact.agency}</div>}
                          {p.contact.phone && <a href={`tel:${p.contact.phone.replace(/[^\d+]/g,"")}`} className="inline-flex items-center gap-1 text-slate-800 min-h-[44px] py-1"><Phone className="w-4 h-4"/>{p.contact.phone}</a>}
                          {p.contact.address && <div className="text-slate-600 text-xs mt-1">{p.contact.address}</div>}
                          {p.contact.website && <a href={p.contact.website} target="_blank" rel="noreferrer" className="text-emerald-800 inline-flex items-center gap-1 text-xs mt-1"><ExternalLink className="w-3 h-3"/>{p.contact.website}</a>}
                        </div>
                      )}
                      <div className="mt-3 flex items-start gap-2 text-xs text-slate-600 bg-slate-50 rounded p-2"><Info className="w-3.5 h-3.5 mt-0.5 shrink-0"/><span>{p.handoff_action}</span></div>
                    </article>
                  ))}
                </div>
                </>
              )}
              <div className="mt-4 text-xs text-slate-500 italic">{result.compliance}</div>
            </section>
          </>
        )}

        <footer className="mt-12 pt-6 border-t border-slate-200 text-xs text-slate-500 leading-relaxed">
          {t.footer_compliance}
        </footer>
      </main>
      <PublicFooter lang={lang} />
    </div>
  );
}
